import { useEmbedForm } from "@circle-react-shared/uikit/TipTapBlockEditor/EmbedExtension/EmbedModal/EmbedModalCompass/EmbedForm";
import type { AddToLocalSgidToObjectMap } from "@circle-react-shared/uikit/TipTapBlockEditor/EmbedExtension/types";
import type { FileRejection } from "@circleco/compass/components/Dropzone";
import { Modal } from "@circleco/compass/components/Modal";
import { Tabs } from "@circleco/compass/components/Tabs";
import type { Editor } from "@tiptap/core";
import { noop } from "lodash";
import { useCallback, useState } from "react";

import { t, toHumanSize } from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";

import {
  DEFAULT_MAX_FILE_UPLOAD_SIZE,
  EMBED_FORM_ID,
  EMBED_TAB,
  UPLOAD_TAB,
} from "./constants";
import { EmbedPanel } from "./EmbedPanel";
import type { FileUploadType } from "./types";
import { UploadPanel } from "./UploadPanel";

interface FileUploadModalComponentProps {
  addToLocalSgidToObjectMap?: AddToLocalSgidToObjectMap;
  editor: Editor;
  maxFileUploadSize?: number;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  range?: { from: number; to: number };
  type?: FileUploadType;
}

export const FileUploadModalComponent = ({
  editor,
  range = { from: 0, to: 0 },
  addToLocalSgidToObjectMap = noop,
  type = "file",
  maxFileUploadSize = DEFAULT_MAX_FILE_UPLOAD_SIZE,
  open,
  onOpenChange,
}: FileUploadModalComponentProps) => {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState(UPLOAD_TAB);

  const isVideo = type === "video";
  const shouldShowEmbedFooter = isVideo && activeTab === EMBED_TAB;

  const {
    url: embedUrl,
    error: embedError,
    handleChange: handleEmbedChange,
    handleSubmit: handleEmbedSubmit,
    isEmpty: isEmbedEmpty,
    isLoading: isEmbedLoading,
  } = useEmbedForm({ addToLocalSgidToObjectMap, editor, range });

  const handleFileRejection = useCallback(
    (rejections: FileRejection[]) => {
      const code = rejections[0]?.errors[0]?.code;
      if (code === "too-many-files") {
        toast.error(t("tiptap.one_file_limit"));
      }
      if (code === "file-too-large") {
        toast.error(
          t("tiptap.file_too_large", {
            size: toHumanSize(maxFileUploadSize),
          }),
        );
      }
    },
    [toast, maxFileUploadSize],
  );

  const handleFilesChange = useCallback(
    (files: File[]) => {
      if (files.length === 0) {
        return;
      }

      editor.chain().focus().deleteRange(range).run();
      files.forEach(file => {
        editor
          .chain()
          .focus()
          .insertContent({ type: "file-loader", attrs: { file } })
          .run();
      });
      onOpenChange(false);
    },
    [editor, range, onOpenChange],
  );

  const tabs = [
    { value: UPLOAD_TAB, label: t("tiptap.upload") },
    { value: EMBED_TAB, label: t("tiptap.embed_link") },
  ];

  return (
    <Modal.Root open={open} onOpenChange={onOpenChange} size="md">
      <Modal.Content>
        <Modal.Header title={t(`tiptap.attach_${type}`)} />
        <Modal.Body>
          {isVideo ? (
            <Tabs.Root
              tabs={tabs}
              size="md"
              selectedValue={activeTab}
              onValueChange={setActiveTab}
            >
              <Tabs.Panel value={UPLOAD_TAB}>
                <UploadPanel
                  maxFileUploadSize={maxFileUploadSize}
                  onFilesChange={handleFilesChange}
                  onFilesRejected={handleFileRejection}
                  type={type}
                />
              </Tabs.Panel>
              <Tabs.Panel value={EMBED_TAB}>
                <EmbedPanel
                  url={embedUrl}
                  error={embedError}
                  onChange={handleEmbedChange}
                  onSubmit={handleEmbedSubmit}
                />
              </Tabs.Panel>
            </Tabs.Root>
          ) : (
            <UploadPanel
              maxFileUploadSize={maxFileUploadSize}
              onFilesChange={handleFilesChange}
              onFilesRejected={handleFileRejection}
              type={type}
            />
          )}
        </Modal.Body>
        {shouldShowEmbedFooter && (
          <Modal.Footer
            primaryAction={{
              type: "submit",
              form: EMBED_FORM_ID,
              label: t("courses.edit.lesson.embed_media"),
              loading: isEmbedLoading,
              disabled: isEmbedEmpty || isEmbedLoading,
            }}
            secondaryAction={{
              label: t("cancel"),
              onClick: () => onOpenChange(false),
            }}
          />
        )}
      </Modal.Content>
    </Modal.Root>
  );
};
